import { useRef } from 'react';
import type { EditorElement } from '../types';
import { FONT_OPTIONS } from '../types';

interface Props {
  selectedElement: EditorElement | null;
  overlayOpacity: number;
  onOverlayOpacityChange: (v: number) => void;
  imageFit: 'cover' | 'contain';
  onImageFitChange: (v: 'cover' | 'contain') => void;
  imagePositionX: number;
  imagePositionY: number;
  onImagePositionXChange: (v: number) => void;
  onImagePositionYChange: (v: number) => void;
  onUpdateElement: (updates: Partial<EditorElement>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onReplaceImage?: (file: File) => void;
}

function Label({ children }: { children: React.ReactNode }) {
  return <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-1.5">{children}</p>;
}

function SliderRow({ label, value, min, max, step = 1, onChange, unit = '' }: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (v: number) => void;
  unit?: string;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-stone-400">{label}</span>
        <span className="text-xs font-bold text-stone-300 tabular-nums">{typeof value === 'number' ? value.toFixed(step < 1 ? 2 : 0) : value}{unit}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-1.5 bg-stone-700 rounded-full appearance-none cursor-pointer accent-orange-500"
      />
    </div>
  );
}

function ColorRow({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-stone-400">{label}</span>
      <div className="flex items-center gap-2">
        <span className="text-xs text-stone-400 font-mono">{value}</span>
        <div className="relative w-8 h-6 rounded-md overflow-hidden border border-stone-600 cursor-pointer">
          <div className="absolute inset-0" style={{ backgroundColor: value === 'transparent' ? 'transparent' : value }}>
            {value === 'transparent' && (
              <div className="w-full h-full" style={{
                backgroundImage: 'linear-gradient(45deg, #555 25%, transparent 25%), linear-gradient(-45deg, #555 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #555 75%), linear-gradient(-45deg, transparent 75%, #555 75%)',
                backgroundSize: '8px 8px',
                backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
              }} />
            )}
          </div>
          <input
            type="color"
            value={value === 'transparent' ? '#000000' : value.startsWith('rgba') ? '#000000' : value}
            onChange={(e) => onChange(e.target.value)}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        </div>
      </div>
    </div>
  );
}

export default function PropertiesPanel({
  selectedElement,
  overlayOpacity,
  onOverlayOpacityChange,
  imageFit,
  onImageFitChange,
  imagePositionX,
  imagePositionY,
  onImagePositionXChange,
  onImagePositionYChange,
  onUpdateElement,
  onDelete,
  onDuplicate,
  onReplaceImage,
}: Props) {
  const replaceImgRef = useRef<HTMLInputElement>(null);
  const updateStyle = (styleUpdates: Partial<EditorElement['style']>) => {
    if (!selectedElement) return;
    onUpdateElement({ style: { ...selectedElement.style, ...styleUpdates } });
  };

  return (
    <div className="w-full md:w-64 bg-stone-900 md:border-l border-stone-800 flex flex-col overflow-y-auto shrink-0">
      {/* Header */}
      <div className="px-4 py-3 border-b border-stone-800 shrink-0">
        <p className="text-xs font-bold text-stone-200 uppercase tracking-widest">
          {selectedElement ? (selectedElement.type === 'button' ? 'Button' : selectedElement.type === 'shape' ? 'Shape' : selectedElement.type === 'image' ? 'Image' : 'Text') + ' Properties' : 'Canvas Properties'}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
        {!selectedElement ? (
          /* Canvas-level controls */
          <div className="space-y-4">
            {/* Image Fit */}
            <div>
              <Label>Image Fit</Label>
              <div className="flex gap-1.5 mb-3">
                <button
                  onClick={() => onImageFitChange('cover')}
                  className={`flex-1 flex flex-col items-center gap-1.5 py-2.5 rounded-lg border text-xs font-medium transition-colors cursor-pointer ${
                    imageFit === 'cover'
                      ? 'bg-orange-500 border-orange-500 text-white'
                      : 'bg-stone-800 border-stone-700 text-stone-400 hover:border-stone-500'
                  }`}
                >
                  <i className="ri-aspect-ratio-fill text-base" />
                  Cover
                </button>
                <button
                  onClick={() => onImageFitChange('contain')}
                  className={`flex-1 flex flex-col items-center gap-1.5 py-2.5 rounded-lg border text-xs font-medium transition-colors cursor-pointer ${
                    imageFit === 'contain'
                      ? 'bg-orange-500 border-orange-500 text-white'
                      : 'bg-stone-800 border-stone-700 text-stone-400 hover:border-stone-500'
                  }`}
                >
                  <i className="ri-fullscreen-line text-base" />
                  Contain
                </button>
              </div>
              <p className="text-xs text-stone-500 mb-3">
                {imageFit === 'cover'
                  ? 'Image fills the frame — use position to choose which part to show'
                  : 'Full image is visible — no cropping'}
              </p>

              {imageFit === 'cover' && (
                <div className="space-y-3">
                  <SliderRow
                    label="Horizontal focus"
                    value={imagePositionX}
                    min={0}
                    max={100}
                    onChange={onImagePositionXChange}
                    unit="%"
                  />
                  <SliderRow
                    label="Vertical focus"
                    value={imagePositionY}
                    min={0}
                    max={100}
                    onChange={onImagePositionYChange}
                    unit="%"
                  />
                  <p className="text-xs text-stone-500">Slide to pan the focal point of the image</p>
                </div>
              )}
            </div>

            {/* Overlay */}
            <div>
              <Label>Image Overlay</Label>
              <SliderRow
                label="Darkness"
                value={Math.round(overlayOpacity * 100)}
                min={0}
                max={85}
                onChange={(v) => onOverlayOpacityChange(v / 100)}
                unit="%"
              />
              <p className="text-xs text-stone-500 mt-1.5">Controls how dark the overlay is on top of your image</p>
            </div>

            <div className="bg-stone-800/60 rounded-xl p-3 space-y-2">
              <p className="text-xs font-semibold text-stone-400">Quick Tips</p>
              <div className="space-y-1.5 text-xs text-stone-500">
                <div className="flex items-start gap-2">
                  <i className="ri-cursor-line text-orange-400 shrink-0 mt-0.5" />
                  <span>Click to select an element</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-drag-move-line text-orange-400 shrink-0 mt-0.5" />
                  <span>Drag to reposition</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-edit-line text-orange-400 shrink-0 mt-0.5" />
                  <span>Double-click to edit text inline</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-paint-line text-orange-400 shrink-0 mt-0.5" />
                  <span>Select element to see style controls here</span>
                </div>
              </div>
            </div>
          </div>
        ) : selectedElement.type === 'image' ? (
          /* Image/logo controls */
          <div className="space-y-5">
            <input ref={replaceImgRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onReplaceImage?.(f); e.target.value = ''; }} />
            <button onClick={() => replaceImgRef.current?.click()} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-stone-600 bg-stone-800 text-stone-300 hover:border-orange-500/50 hover:text-white text-xs font-semibold transition-colors cursor-pointer">
              <i className="ri-image-edit-line text-orange-400" /> Replace Image
            </button>
            <div>
              <Label>Size</Label>
              <div className="space-y-2">
                <SliderRow label="Width" value={selectedElement.style.width ?? 30} min={5} max={90} onChange={(v) => updateStyle({ width: v })} unit="%" />
                <SliderRow label="Height" value={selectedElement.style.height ?? 20} min={3} max={80} onChange={(v) => updateStyle({ height: v })} unit="%" />
              </div>
            </div>
            <div>
              <Label>Style</Label>
              <div className="space-y-2">
                <SliderRow label="Opacity" value={Math.round(selectedElement.style.opacity * 100)} min={10} max={100} onChange={(v) => updateStyle({ opacity: v / 100 })} unit="%" />
                <SliderRow label="Border radius" value={selectedElement.style.borderRadius} min={0} max={50} onChange={(v) => updateStyle({ borderRadius: v })} unit="px" />
              </div>
            </div>
            <div>
              <Label>Element State</Label>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => onUpdateElement({ locked: !selectedElement.locked })} className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-medium transition-colors cursor-pointer ${selectedElement.locked ? 'bg-amber-500/20 border-amber-500/40 text-amber-300' : 'bg-stone-800 border-stone-700 text-stone-400 hover:border-stone-600'}`}>
                  <i className={selectedElement.locked ? 'ri-lock-fill' : 'ri-lock-unlock-line'} />{selectedElement.locked ? 'Locked' : 'Lock'}
                </button>
                <button onClick={() => onUpdateElement({ visible: !selectedElement.visible })} className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-medium transition-colors cursor-pointer ${!selectedElement.visible ? 'bg-stone-700 border-stone-600 text-stone-500' : 'bg-stone-800 border-stone-700 text-stone-400 hover:border-stone-600'}`}>
                  <i className={selectedElement.visible ? 'ri-eye-line' : 'ri-eye-off-line'} />{selectedElement.visible ? 'Visible' : 'Hidden'}
                </button>
              </div>
            </div>
            <div className="pt-1 border-t border-stone-800 space-y-2">
              <button onClick={onDuplicate} className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-stone-700 text-stone-400 hover:border-stone-500 hover:text-stone-300 text-xs font-medium transition-colors cursor-pointer"><i className="ri-file-copy-line" />Duplicate</button>
              <button onClick={onDelete} className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-red-900/40 text-red-400 hover:bg-red-900/20 hover:border-red-700 text-xs font-medium transition-colors cursor-pointer"><i className="ri-delete-bin-line" />Delete</button>
            </div>
          </div>
        ) : (
          /* Element controls */
          <div className="space-y-5">
            {/* Content */}
            {selectedElement.type !== 'shape' && (
              <div>
                <Label>Content</Label>
                <textarea
                  value={selectedElement.content}
                  onChange={(e) => onUpdateElement({ content: e.target.value })}
                  rows={2}
                  className="w-full text-sm px-3 py-2 rounded-lg bg-stone-800 border border-stone-700 text-stone-200 placeholder:text-stone-500 focus:outline-none focus:border-orange-500 resize-none"
                  placeholder="Enter text..."
                />
              </div>
            )}

            {/* Font */}
            <div>
              <Label>Font</Label>
              <div className="space-y-2">
                <select
                  value={selectedElement.style.fontFamily}
                  onChange={(e) => updateStyle({ fontFamily: e.target.value })}
                  className="w-full text-xs px-3 py-2 rounded-lg bg-stone-800 border border-stone-700 text-stone-200 focus:outline-none focus:border-orange-500 cursor-pointer"
                >
                  {FONT_OPTIONS.map((f) => (
                    <option key={f.value} value={f.value} style={{ fontFamily: f.value }}>
                      {f.label}
                    </option>
                  ))}
                </select>

                <div className="grid grid-cols-3 gap-1.5">
                  {([100, 400, 500, 600, 700, 800, 900] as number[]).map((w) => (
                    <button
                      key={w}
                      onClick={() => updateStyle({ fontWeight: w })}
                      className={`py-1.5 rounded-md text-xs transition-colors cursor-pointer whitespace-nowrap ${
                        selectedElement.style.fontWeight === w
                          ? 'bg-orange-500 text-white'
                          : 'bg-stone-800 border border-stone-700 text-stone-400 hover:border-stone-500'
                      }`}
                      style={{ fontWeight: w }}
                    >
                      {w}
                    </button>
                  ))}
                </div>

                <SliderRow
                  label="Size"
                  value={selectedElement.style.fontSize}
                  min={8}
                  max={72}
                  onChange={(v) => updateStyle({ fontSize: v })}
                  unit="px"
                />

                <SliderRow
                  label="Letter spacing"
                  value={selectedElement.style.letterSpacing}
                  min={-2}
                  max={10}
                  step={0.5}
                  onChange={(v) => updateStyle({ letterSpacing: v })}
                  unit="px"
                />

                <SliderRow
                  label="Line height"
                  value={selectedElement.style.lineHeight}
                  min={0.8}
                  max={3}
                  step={0.05}
                  onChange={(v) => updateStyle({ lineHeight: v })}
                />
              </div>
            </div>

            {/* Text align + transform */}
            {selectedElement.type !== 'shape' && (
              <div>
                <Label>Alignment</Label>
                <div className="flex gap-1.5">
                  {(['left', 'center', 'right'] as const).map((align) => (
                    <button
                      key={align}
                      onClick={() => updateStyle({ textAlign: align })}
                      className={`flex-1 h-8 flex items-center justify-center rounded-lg border transition-colors cursor-pointer ${
                        selectedElement.style.textAlign === align
                          ? 'bg-orange-500 border-orange-500 text-white'
                          : 'bg-stone-800 border-stone-700 text-stone-400 hover:border-stone-500'
                      }`}
                    >
                      <i className={`ri-align-${align} text-sm`} />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Colors */}
            <div>
              <Label>Colors</Label>
              <div className="space-y-2 bg-stone-800/50 rounded-xl p-3">
                <ColorRow
                  label="Text color"
                  value={selectedElement.style.color}
                  onChange={(v) => updateStyle({ color: v })}
                />
                <ColorRow
                  label="Background"
                  value={selectedElement.style.backgroundColor}
                  onChange={(v) => updateStyle({ backgroundColor: v })}
                />
                <button
                  onClick={() => updateStyle({ backgroundColor: 'transparent' })}
                  className="text-xs text-stone-500 hover:text-orange-400 transition-colors cursor-pointer"
                >
                  Set BG transparent
                </button>
              </div>
            </div>

            {/* Shape / button controls */}
            {(selectedElement.type === 'button' || selectedElement.type === 'shape') && (
              <div>
                <Label>Shape</Label>
                <div className="space-y-2">
                  <SliderRow
                    label="Border radius"
                    value={selectedElement.style.borderRadius}
                    min={0}
                    max={50}
                    onChange={(v) => updateStyle({ borderRadius: v })}
                    unit="px"
                  />
                  <SliderRow
                    label="Pad horizontal"
                    value={selectedElement.style.paddingX}
                    min={0}
                    max={60}
                    onChange={(v) => updateStyle({ paddingX: v })}
                    unit="px"
                  />
                  <SliderRow
                    label="Pad vertical"
                    value={selectedElement.style.paddingY}
                    min={0}
                    max={40}
                    onChange={(v) => updateStyle({ paddingY: v })}
                    unit="px"
                  />
                  <SliderRow
                    label="Width"
                    value={selectedElement.style.width ?? 28}
                    min={5}
                    max={90}
                    onChange={(v) => updateStyle({ width: v })}
                    unit="%"
                  />
                </div>
              </div>
            )}

            {/* Effects */}
            <div>
              <Label>Effects</Label>
              <div className="space-y-2">
                <SliderRow
                  label="Opacity"
                  value={Math.round(selectedElement.style.opacity * 100)}
                  min={10}
                  max={100}
                  onChange={(v) => updateStyle({ opacity: v / 100 })}
                  unit="%"
                />

                {selectedElement.type !== 'shape' && (
                  <button
                    onClick={() => updateStyle({ textShadow: !selectedElement.style.textShadow })}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border transition-colors cursor-pointer ${
                      selectedElement.style.textShadow
                        ? 'bg-orange-500/20 border-orange-500/40 text-orange-300'
                        : 'bg-stone-800 border-stone-700 text-stone-400 hover:border-stone-600'
                    }`}
                  >
                    <span className="text-xs font-medium">Text shadow</span>
                    <i className={`${selectedElement.style.textShadow ? 'ri-toggle-fill text-orange-400' : 'ri-toggle-line'} text-lg`} />
                  </button>
                )}
              </div>
            </div>

            {/* Lock / visibility */}
            <div>
              <Label>Element State</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => onUpdateElement({ locked: !selectedElement.locked })}
                  className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-medium transition-colors cursor-pointer ${
                    selectedElement.locked
                      ? 'bg-amber-500/20 border-amber-500/40 text-amber-300'
                      : 'bg-stone-800 border-stone-700 text-stone-400 hover:border-stone-600'
                  }`}
                >
                  <i className={selectedElement.locked ? 'ri-lock-fill' : 'ri-lock-unlock-line'} />
                  {selectedElement.locked ? 'Locked' : 'Lock'}
                </button>
                <button
                  onClick={() => onUpdateElement({ visible: !selectedElement.visible })}
                  className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-medium transition-colors cursor-pointer ${
                    !selectedElement.visible
                      ? 'bg-stone-700 border-stone-600 text-stone-500'
                      : 'bg-stone-800 border-stone-700 text-stone-400 hover:border-stone-600'
                  }`}
                >
                  <i className={selectedElement.visible ? 'ri-eye-line' : 'ri-eye-off-line'} />
                  {selectedElement.visible ? 'Visible' : 'Hidden'}
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="pt-1 border-t border-stone-800 space-y-2">
              <button
                onClick={onDuplicate}
                className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-stone-700 text-stone-400 hover:border-stone-500 hover:text-stone-300 text-xs font-medium transition-colors cursor-pointer"
              >
                <i className="ri-file-copy-line" />
                Duplicate Element
              </button>
              <button
                onClick={onDelete}
                className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-red-900/40 text-red-400 hover:bg-red-900/20 hover:border-red-700 text-xs font-medium transition-colors cursor-pointer"
              >
                <i className="ri-delete-bin-line" />
                Delete Element
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
